/**
 * Subscription Context (Access Pass Model)
 * Manages user Access Pass state and feature access throughout the app
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import * as api from '../utils/api';

const SubscriptionContext = createContext(null);

// Default permissions for non-authenticated users
const DEFAULT_PERMISSIONS = {
  can_use_quick_estimate: true,
  can_use_material_estimate: true,
  can_save_project: false,
  can_compare_scenarios: false,
  can_export_pdf: false,
  can_use_full_breakdown: true,
  has_unlimited_estimates: false,
};

// Default usage for non-authenticated users
const DEFAULT_USAGE = {
  quick_estimates_used: 0,
  quick_estimates_remaining: 2, // Free users get 2 basic estimates
  quick_estimates_limit: 2,
  saved_projects: 0,
  max_projects: 0,
  max_phases: 2,
  total_estimates: 0,
};

// Default time remaining (for expired/no pass)
const DEFAULT_TIME_REMAINING = {
  hours: 0,
  minutes: 0,
  seconds: 0,
  expired: true,
  total_seconds: 0,
};

// Legacy localStorage keys for guest usage (kept for backward compat)
const GUEST_USAGE_KEY = 'rumahkira_free_uses_count';
const GUEST_RESET_TIME_KEY = 'rumahkira_free_uses_reset_time';

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [accessPass, setAccessPass] = useState(null);
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [usage, setUsage] = useState(DEFAULT_USAGE);
  const [timeRemaining, setTimeRemaining] = useState(DEFAULT_TIME_REMAINING);
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState({});
  const [actionLimits, setActionLimits] = useState(null); // Per-action gate limits from server
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const countdownRef = useRef(null);
  const fetchStatusRef = useRef(null);

  // Get guest usage count from localStorage (simple counter, shared with useFreeTrial)
  // Also checks for 24h reset
  const getGuestUsage = useCallback(() => {
    try {
      const resetTime = localStorage.getItem(GUEST_RESET_TIME_KEY);
      if (resetTime) {
        const elapsed = Date.now() - parseInt(resetTime, 10);
        if (elapsed >= 24 * 60 * 60 * 1000) {
          localStorage.setItem(GUEST_USAGE_KEY, '0');
          localStorage.setItem(GUEST_RESET_TIME_KEY, Date.now().toString());
          return { quick_estimates: 0 };
        }
      } else {
        localStorage.setItem(GUEST_RESET_TIME_KEY, Date.now().toString());
      }
      const count = parseInt(localStorage.getItem(GUEST_USAGE_KEY) || '0', 10);
      return { quick_estimates: count };
    } catch {
      return { quick_estimates: 0 };
    }
  }, []);

  // Save guest usage count to localStorage (simple counter)
  const saveGuestUsage = useCallback((usage) => {
    try {
      localStorage.setItem(GUEST_USAGE_KEY, usage.quick_estimates.toString());
    } catch {
      // Ignore storage errors
    }
  }, []);

  // Start countdown timer for active Access Pass
  const startCountdown = useCallback((expiresAt) => {
    // Clear existing countdown
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    if (!expiresAt) {
      setTimeRemaining(DEFAULT_TIME_REMAINING);
      return;
    }

    const updateCountdown = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const diff = expiry - now;

      if (diff <= 0) {
        setTimeRemaining({ ...DEFAULT_TIME_REMAINING, expired: true });
        clearInterval(countdownRef.current);
        // Refresh status when pass expires
        if (fetchStatusRef.current) fetchStatusRef.current();
        return;
      }

      const totalSeconds = Math.floor(diff / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      setTimeRemaining({
        hours,
        minutes,
        seconds,
        expired: false,
        total_seconds: totalSeconds,
        formatted: hours > 24
          ? `${Math.floor(hours / 24)}d ${hours % 24}h`
          : `${hours}h ${minutes}m`,
      });
    };

    // Update immediately
    updateCountdown();

    // Update every second for accurate countdown
    countdownRef.current = setInterval(updateCountdown, 1000);
  }, []);

  // Cleanup countdown on unmount
  useEffect(() => {
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  // Fetch subscription/Access Pass status
  const fetchSubscriptionStatus = useCallback(async () => {
    if (!api.isAuthenticated()) {
      // For guests, use local storage tracking
      const guestUsage = getGuestUsage();
      setUsage({
        ...DEFAULT_USAGE,
        quick_estimates_used: guestUsage.quick_estimates,
        quick_estimates_remaining: Math.max(0, 2 - guestUsage.quick_estimates),
      });
      setPermissions({
        ...DEFAULT_PERMISSIONS,
        can_use_quick_estimate: guestUsage.quick_estimates < 2,
      });
      setAccessPass(null);
      setTimeRemaining(DEFAULT_TIME_REMAINING);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.getSubscriptionStatus();

      if (response.success) {
        const data = response.data;

        // Set Access Pass info
        if (data.access_pass) {
          setAccessPass(data.access_pass);

          // Start countdown if pass is active
          if (data.access_pass.is_active && data.access_pass.expires_at) {
            startCountdown(data.access_pass.expires_at);
          } else {
            setTimeRemaining(DEFAULT_TIME_REMAINING);
          }
        }

        // Set time remaining from server
        if (data.time_remaining) {
          setTimeRemaining(data.time_remaining);
        }

        setSubscription(data.subscription);
        setPermissions(data.permissions);
        setUsage(data.usage);

        // Store per-action limits from gate system
        if (data.action_limits) {
          setActionLimits(data.action_limits);
        }
      }
    } catch (err) {
      console.error('Failed to fetch Access Pass status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getGuestUsage, startCountdown]);

  // Keep ref in sync for countdown callback
  fetchStatusRef.current = fetchSubscriptionStatus;

  // Fetch Access Pass plans
  const fetchPlans = useCallback(async () => {
    try {
      const response = await api.getSubscriptionPlans();

      if (response.success) {
        setPlans(response.data.plans);
        setFeatures(response.data.features);
      }
    } catch (err) {
      console.error('Failed to fetch Access Pass plans:', err);
    }
  }, []);

  // Initialize
  useEffect(() => {
    fetchSubscriptionStatus();
    fetchPlans();
  }, [fetchSubscriptionStatus, fetchPlans]);

  // Check if user can use a specific feature
  const canUseFeature = useCallback((featureCode) => {
    switch (featureCode) {
      case 'quick_estimate':
      case 'basic_estimate':
        return permissions.can_use_quick_estimate;
      case 'material_estimator':
        return permissions.can_use_material_estimate;
      case 'save_projects':
        return permissions.can_save_project;
      case 'compare_scenarios':
        return permissions.can_compare_scenarios;
      case 'pdf_export':
        return permissions.can_export_pdf;
      case 'full_breakdown':
        return permissions.can_use_full_breakdown;
      default:
        return true;
    }
  }, [permissions]);

  // Record feature usage and update state
  const recordFeatureUsage = useCallback(async (featureCode, metadata = null) => {
    if (!api.isAuthenticated()) {
      // For guests, track locally
      const guestUsage = getGuestUsage();

      if (featureCode === 'quick_estimate' || featureCode === 'basic_estimate') {
        guestUsage.quick_estimates += 1;
        saveGuestUsage(guestUsage);
        setUsage(prev => ({
          ...prev,
          quick_estimates_used: guestUsage.quick_estimates,
          quick_estimates_remaining: Math.max(0, 2 - guestUsage.quick_estimates),
        }));
        setPermissions(prev => ({
          ...prev,
          can_use_quick_estimate: guestUsage.quick_estimates < 2,
        }));
      }

      return { success: true, remaining: -1 };
    }

    try {
      const response = await api.useFeature(featureCode, metadata);

      if (response.success) {
        // Refresh subscription status after using feature
        await fetchSubscriptionStatus();
        return response;
      }

      return response;
    } catch (err) {
      // Check if it's a limit/feature error
      if (err.status === 403) {
        return {
          success: false,
          code: err.message.includes('limit') ? 'LIMIT_REACHED' : 'FEATURE_LOCKED',
          message: err.message,
          upgrade_required: true,
        };
      }
      throw err;
    }
  }, [getGuestUsage, saveGuestUsage, fetchSubscriptionStatus]);

  // Create payment and get payment URL for Access Pass
  const initiatePayment = useCallback(async (planCode) => {
    try {
      const response = await api.createPayment(planCode);
      return response;
    } catch (err) {
      console.error('Failed to create payment:', err);
      throw err;
    }
  }, []);

  // Verify payment after return from payment gateway
  const checkPayment = useCallback(async (reference, billCode = null) => {
    try {
      const response = await api.verifyPayment(reference, billCode);

      if (response.success && response.data.payment.status === 'completed') {
        // Refresh Access Pass status
        await fetchSubscriptionStatus();
      }

      return response;
    } catch (err) {
      console.error('Failed to verify payment:', err);
      throw err;
    }
  }, [fetchSubscriptionStatus]);

  // Refresh subscription (call after login/signup)
  const refreshSubscription = useCallback(async () => {
    await fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  // Check if user is on free plan
  const isFreePlan = accessPass?.type === 'free' || !accessPass;

  // Check if user has active Access Pass
  const hasActivePass = accessPass?.is_active && !timeRemaining?.expired;

  // Get Access Pass name
  const getPassName = useCallback((language = 'en') => {
    if (!accessPass || isFreePlan) {
      return language === 'my' ? 'Percuma' : 'Free';
    }
    return language === 'my' ? accessPass.plan_name_my : accessPass.plan_name;
  }, [accessPass, isFreePlan]);

  // Get remaining usage text
  const getRemainingText = useCallback((featureCode, language = 'en') => {
    const remaining = usage.quick_estimates_remaining;
    const limit = usage.quick_estimates_limit;

    if (remaining === -1 || limit === -1) {
      return language === 'my' ? 'Tanpa had' : 'Unlimited';
    }

    return language === 'my'
      ? `${remaining}/${limit} baki`
      : `${remaining}/${limit} remaining`;
  }, [usage]);

  // Get time remaining text
  const getTimeRemainingText = useCallback((language = 'en') => {
    if (!timeRemaining || timeRemaining.expired) {
      return language === 'my' ? 'Tamat tempoh' : 'Expired';
    }

    if (timeRemaining.hours > 24) {
      const days = Math.floor(timeRemaining.hours / 24);
      const hours = timeRemaining.hours % 24;
      return language === 'my'
        ? `${days}h ${hours}j lagi`
        : `${days}d ${hours}h left`;
    }

    return language === 'my'
      ? `${timeRemaining.hours}j ${timeRemaining.minutes}m lagi`
      : `${timeRemaining.hours}h ${timeRemaining.minutes}m left`;
  }, [timeRemaining]);

  const value = {
    // State
    subscription,
    accessPass,
    permissions,
    usage,
    timeRemaining,
    plans,
    features,
    actionLimits,
    loading,
    error,

    // Computed
    isFreePlan,
    hasActivePass,
    isPremium: hasActivePass, // Legacy compatibility

    // Methods
    canUseFeature,
    recordFeatureUsage,
    initiatePayment,
    checkPayment,
    refreshSubscription,
    getRemainingText,
    getTimeRemainingText,
    getPassName,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// Custom hook to use subscription context
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
